import { notifications } from '@mantine/notifications'
import type { AppRouter } from '@markku/backend/trpc/router'
import type {
  BucketType,
  CreateAttachmentResponse,
  AttachmentResponse,
  QueryOptions,
} from '@markku/types'
import type { CreateTRPCReact } from '@trpc/react-query'

export interface AttachmentAction {
  id: number
  action: 'add' | 'delete'
}

/**
 * Utility function to upload file to S3 with progress tracking
 */
export async function uploadFileToS3({
  file,
  uploadUrl,
  fields,
  onProgress,
}: {
  file: File
  uploadUrl: string
  fields: Record<string, string>
  onProgress?: (progress: number) => void
}): Promise<{ success: boolean; error?: string }> {
  return new Promise((resolve) => {
    try {
      const formData = new FormData()
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value)
      })
      formData.append('file', file)

      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true })
        } else {
          resolve({
            success: false,
            error: `S3-lataus epäonnistui: ${xhr.status} ${xhr.statusText}`,
          })
        }
      })

      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: 'S3-lataus epäonnistui',
        })
      })

      xhr.open('POST', uploadUrl)
      xhr.send(formData)
    } catch (error) {
      resolve({
        success: false,
        error: error instanceof Error ? error.message : 'S3-lataus epäonnistui',
      })
    }
  })
}

type UploadFileParams = {
  file: File
  entityType: string
  entityId: number
  bucket?: BucketType
  description?: string
  metadata?: Record<string, unknown>
  onProgress?: (progress: number) => void
  requestUploadMutateAsync: (input: {
    entityType: string
    entityId: number
    bucket: BucketType
    filename: string
    fileSize: number
    description?: string
    metadata?: Record<string, unknown>
  }) => Promise<CreateAttachmentResponse>
}

type UploadFileResult = {
  success: boolean
  attachment?: Partial<AttachmentResponse>
  error?: string
}

/**
 * Utility function to upload file with progress
 */
export async function uploadFile({
  file,
  entityType,
  entityId,
  bucket = 'private',
  description,
  metadata,
  onProgress,
  requestUploadMutateAsync,
}: UploadFileParams): Promise<UploadFileResult> {
  try {
    onProgress?.(10)

    const uploadUrlResponse = await requestUploadMutateAsync({
      entityType,
      entityId,
      bucket,
      filename: file.name,
      fileSize: file.size,
      description,
      metadata,
    })

    onProgress?.(30)

    const uploadResult = await uploadFileToS3({
      file,
      uploadUrl: uploadUrlResponse.uploadUrl,
      fields: uploadUrlResponse.fields,
    })

    if (!uploadResult.success) {
      return uploadResult
    }

    onProgress?.(80)

    const attachment = {
      id: uploadUrlResponse.id,
      filename: file.name,
      filesize: file.size,
      entityType,
      entityId,
    }

    onProgress?.(100)

    return { success: true, attachment }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Lataus epäonnistui',
    }
  }
}

/**
 * Creates attachment-related hooks with the provided tRPC client.
 * Full type safety is preserved - all return types are inferred from the AppRouter.
 *
 * @param trpc - The typed tRPC client from the consuming app
 * @returns Object containing all attachment-related hooks with full type inference
 */
export function createAttachmentHooks(
  trpc: CreateTRPCReact<AppRouter, unknown>,
) {
  return {
    useGetByEntity: (
      entityType: string,
      entityId: number,
      options?: QueryOptions,
    ) => {
      return trpc.attachment.getByEntity.useQuery(
        { entityType, entityId },
        {
          enabled: !!entityType && !!entityId,
          staleTime: 5 * 60 * 1000,
          ...options,
        },
      )
    },

    useCreateAttachment: () => {
      const utils = trpc.useUtils()
      const mutation = trpc.attachment.createAttachment.useMutation({
        onSuccess: (_, variables) => {
          if (variables.entityType && variables.entityId) {
            utils.attachment.getByEntity.invalidate({
              entityType: variables.entityType,
              entityId: variables.entityId,
            })
          }
        },
        onError: (error) => {
          notifications.show({
            title: 'Virhe',
            message: error.message || 'Latausosoitteen pyyntö epäonnistui',
            color: 'red',
          })
        },
      })

      return mutation
    },

    // Alias for compatibility with components that use this naming
    useUploadAttachment: {
      getMutation: (opts?: { showErrorNotification?: boolean }) => {
        const showErrorNotification = opts?.showErrorNotification ?? true
        return trpc.attachment.createAttachment.useMutation({
          onError: (error) => {
            if (!showErrorNotification) return
            notifications.show({
              title: 'Lataus epäonnistui',
              message: error.message || 'Tiedoston lataus epäonnistui',
              color: 'red',
            })
          },
        })
      },
    },

    useUpdate: () => {
      const utils = trpc.useUtils()
      return trpc.attachment.update.useMutation({
        onSuccess: (attachment) => {
          notifications.show({
            title: 'Onnistui',
            message: 'Liite päivitetty onnistuneesti',
            color: 'green',
          })
          utils.attachment.getByEntity.invalidate({
            entityType: attachment.entityType,
            entityId: attachment.entityId,
          })
        },
        onError: (error) => {
          notifications.show({
            title: 'Päivitys epäonnistui',
            message: error.message || 'Liitteen päivitys epäonnistui',
            color: 'red',
          })
        },
      })
    },

    useDelete: () => {
      const utils = trpc.useUtils()
      const mutation = trpc.attachment.delete.useMutation({
        onSuccess: (attachment) => {
          notifications.show({
            title: 'Onnistui',
            message: `Tiedosto "${attachment.filename}" poistettu onnistuneesti`,
            color: 'green',
          })
          utils.attachment.getByEntity.invalidate({
            entityType: attachment.entityType,
            entityId: attachment.entityId,
          })
        },
        onError: (error) => {
          notifications.show({
            title: 'Poisto epäonnistui',
            message: error.message || 'Liitteen poisto epäonnistui',
            color: 'red',
          })
        },
      })

      return mutation
    },
  }
}

export type AttachmentHooks = ReturnType<typeof createAttachmentHooks>
